import React, { useEffect, useState, useRef, useMemo } from "react";
import { VscChromeClose } from "react-icons/vsc";
import { IoShareSocial } from "react-icons/io5";
import { FaSearch, FaEye } from "react-icons/fa";
import { HiOutlineDocumentText } from "react-icons/hi";
import moment from "moment";
import Loader from "../../../components/Loader";
import { useNavigate } from "react-router-dom";

// Adjust the import paths to properly reference services and utils
import KidService from "../../../services/kids";
import ExpencesService from "../../../services/expences";
import decodeToken from "../../../utils/decodeToken";
import {
  DOCUMENT_FILTER_OPTIONS,
  filterDocuments,
  sortDocuments,
} from "../../../utils/documentCategory";
import onRazorpay from "../../checkout/RazorPay";
import "./KidProfileModal.css";

// Ensure modal sizing and overlay match sponsor modal.
const KidProfileModal = ({
  isOpen,
  onClose,
  kidId,
  onSponsorClick,
  isSponsoredKid,
}) => {
  const modalRef = useRef(null);
  const [profile, setProfile] = useState({});
  const [expences, setExpences] = useState([]);
  const [kidPhotos, setKidPhotos] = useState([]);
  const [kidDocuments, setKidDocuments] = useState([]);
  const [isImgFull, setIsImgFull] = useState("");
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [photoModalIndex, setPhotoModalIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [activeExpense, setActiveExpense] = useState("basic");
  const [selectedExpenses, setSelectedExpenses] = useState(["basic"]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [expandedCards, setExpandedCards] = useState({});
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [documentSearch, setDocumentSearch] = useState("");
  const [documentFilter, setDocumentFilter] = useState("all");
  const [documentSort, setDocumentSort] = useState("latest");

  const { user_id, super_admin_id } = decodeToken();
  let params = new URLSearchParams(window?.location?.search);

  const visibleDocuments = useMemo(() => {
    const filtered = filterDocuments(kidDocuments, {
      search: documentSearch,
      category: documentFilter,
    });
    return sortDocuments(filtered, documentSort);
  }, [kidDocuments, documentSearch, documentFilter, documentSort]);

  const handleViewDocument = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const navigate = useNavigate();

  // Helper function to get only first two words
  const getFirstTwoWords = (text) => {
    if (!text) return "";
    const words = text.split(" ");
    return words.slice(0, 2).join(" ");
  };

  // Helper function to calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return "";
    const birthYear = moment(dateOfBirth).year();
    const currentYear = moment().year();
    return `${currentYear - birthYear}Y`;
  };

  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
  };

  const handleExpenseClick = (expenseType) => {
    setActiveExpense(expenseType);
  };

  useEffect(() => {
    if (!isOpen) {
      setDocumentSearch("");
      setDocumentFilter("all");
      setDocumentSort("latest");
    }
  }, [isOpen]);

  // Handle body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      if (window.innerWidth <= 768) {
        document.body.classList.add("kpm__modal-page-open");
      }
    } else {
      document.body.style.overflow = "auto";
      document.body.classList.remove("kpm__modal-page-open");
    }
    return () => {
      document.body.style.overflow = "auto";
      document.body.classList.remove("kpm__modal-page-open");
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setShareMenuOpen(false);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && kidId) {
      setLoading(true);
      Promise.all([
        KidService.fetchKidById(kidId),
        ExpencesService.fetchExpencesKid(kidId),
      ])
        .then(([kidRes, expRes]) => {
          setProfile(kidRes.data.data[0]);
          setExpences(expRes.data.data);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [isOpen, kidId]);

  useEffect(() => {
    if (!isOpen || !kidId) return;
    const refreshProfile = () => {
      if (document.visibilityState !== "visible") return;
      KidService.fetchKidById(kidId)
        .then(({ data }) => setProfile(data.data[0]))
        .catch(() => {});
    };
    document.addEventListener("visibilitychange", refreshProfile);
    return () =>
      document.removeEventListener("visibilitychange", refreshProfile);
  }, [isOpen, kidId]);

  useEffect(() => {
    if ((user_id || super_admin_id) && profile.enrollment_id) {
      setMediaLoading(true);
      Promise.all([
        KidService.fetchPhotosByKidId({ enrollment_id: profile.enrollment_id }),
        KidService.fetchDocumentsByKidId({
          enrollment_id: profile.enrollment_id,
        }),
      ])
        .then(([photosRes, docsRes]) => {
          setKidPhotos(photosRes.data.data);
          setKidDocuments(docsRes.data.data);
        })
        .catch(() => {})
        .finally(() => setMediaLoading(false));
    }
  }, [profile.enrollment_id]);

  // In production use REACT_APP_SITE_URL; in dev fallback to current origin (e.g. http://localhost:3001)
  const baseUrl = (
    process.env.REACT_APP_SITE_URL ||
    (typeof window !== "undefined" && window.location.origin) ||
    ""
  ).replace(/\/$/, "");
  const sponsorLink = `${baseUrl}/kid/${kidId}`;

  const totalExpense = useMemo(() => {
    let total = profile?.basic_amount || 0;
    if (expences?.education?.length > 0) {
      expences.education.forEach((item) => {
        total += Number(item.amount) || 0;
      });
    }
    if (expences?.curriculum?.length > 0) {
      expences.curriculum.forEach((item) => {
        total += Number(item.amount) || 0;
      });
    }
    if (expences?.extra_curriculum?.length > 0) {
      expences.extra_curriculum.forEach((item) => {
        total += Number(item.amount) || 0;
      });
    }
    return total;
  }, [profile, expences]);

  const shareMessage = useMemo(() => {
    if (!profile?.name) return "";
    const age = calculateAge(profile.date_of_birth);
    const nameLine = `${profile.name}${age ? ` (${age})` : ""} -`;
    const categoryLine = profile.category_name || "";
    const expenseLine = `Monthly expense: ₹ ${totalExpense}/month. Sponsor here: ${sponsorLink}`;
    return [nameLine, categoryLine, expenseLine].filter(Boolean).join("\n");
  }, [
    profile?.name,
    profile?.date_of_birth,
    profile?.category_name,
    totalExpense,
    sponsorLink,
  ]);

  // Function to calculate total amount from selected expenses
  const calculateSelectedExpensesTotal = () => {
    let total = 0;

    // Add basic expense if selected
    if (selectedExpenses.includes("basic")) {
      total += Number(profile?.basic_amount || 0);
    }

    // Add educational expenses if selected
    if (
      selectedExpenses.includes("educational") &&
      expences?.education?.length > 0
    ) {
      expences.education.forEach((item) => {
        total += Number(item.amount || 0);
      });
    }

    // Add co-curricular expenses if selected
    if (
      selectedExpenses.includes("co-curricular") &&
      expences?.curriculum?.length > 0
    ) {
      expences.curriculum.forEach((item) => {
        total += Number(item.amount || 0);
      });
    }

    // Add activity expenses if selected
    if (
      selectedExpenses.includes("activity") &&
      expences?.extra_curriculum?.length > 0
    ) {
      expences.extra_curriculum.forEach((item) => {
        total += Number(item.amount || 0);
      });
    }

    return total;
  };

  // Function to handle payment via Razorpay
  const handlePayment = async () => {
    try {
      setPaymentLoading(true);
      const amount = calculateSelectedExpensesTotal();

      if (amount <= 0) {
        alert("Please select at least one expense to sponsor");
        setPaymentLoading(false);
        return;
      }

      // Close the profile modal and open sponsor modal
      onClose();
      if (onSponsorClick) {
        onSponsorClick(kidId, amount); // Pass the total amount
      }
    } catch (error) {
      console.error("Payment error:", error);
      setPaymentLoading(false);
      alert(
        `Payment initialization failed: ${error.message || "Unknown error"}`,
      );
    } finally {
      setPaymentLoading(false);
    }
  };

  const toggleCardDropdown = (cardType) => {
    setExpandedCards((prev) => ({
      ...prev,
      [cardType]: !prev[cardType],
    }));
  };

  // Handle click outside to close modal
  const handleClickOutside = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  const ProfileContent = () => {
    return (
      <div className="kmp__tab-content">
        <p className="kmp__TabTitle">Profile Info</p>
        <p className="kmp__bio-text">{profile.bio}</p>
        {/* Profile Details in New Layout */}
        <div className="kmp__profile-details-grid">
          <div className="kmp__profile-detail-row">
            <div
              className="kmp__profile-detail-item"
              style={{ paddingTop: "10px" }}
            >
              <p className="kmp__detail-heading">Age:</p>
              <p className="kmp__detail-value">
                {calculateAge(profile.date_of_birth)}
              </p>
            </div>
            <div className="kmp__profile-detail-item">
              <p className="kmp__detail-heading">Location:</p>
              <p className="kmp__detail-value">
                {getFirstTwoWords(profile.address)}
              </p>
            </div>
            {profile.shaagird_nama && (
              <div className="kmp__profile-detail-item">
                <p className="kmp__detail-heading">Shaagirdnama:</p>
                <p className="kmp__detail-value">
                  <a
                    href={profile.shaagird_nama}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#299D8E",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    {profile.shaagird_nama}
                  </a>
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="kmp__profile-info-btns">
          <div className="kmp__info-btn-Expense">
            <p className="kmp__expTitle">Monthly Expense</p>
            <p className="kmp__expVal">₹ {totalExpense}/month</p>
          </div>
        </div>
      </div>
    );
  };

  const ExpenseContent = () => {
    const totalBasicExpense = profile?.basic_amount || 0;

    const totalEducationExpense =
      expences?.education?.length > 0
        ? expences.education.reduce((sum, item) => sum + Number(item.amount), 0)
        : 0;

    const totalCurriculumExpense =
      expences?.curriculum?.length > 0
        ? expences.curriculum.reduce(
            (sum, item) => sum + Number(item.amount),
            0,
          )
        : 0;

    const totalActivityExpense =
      expences?.extra_curriculum?.length > 0
        ? expences.extra_curriculum.reduce(
            (sum, item) => sum + Number(item.amount),
            0,
          )
        : 0;

    const handleExpenseSelection = (expenseType) => {
      setSelectedExpenses((prev) => {
        if (prev.includes(expenseType)) {
          return prev.filter((exp) => exp !== expenseType);
        } else {
          return [...prev, expenseType];
        }
      });
    };

    const isExpenseSelected = (expenseType) => {
      return selectedExpenses.includes(expenseType);
    };

    const selectedTotal = calculateSelectedExpensesTotal();

    return (
      <div className="kpm__tab-content">
        <div className="kpm__expense-cards">
          <button
            className={`kpm__expense-card ${isExpenseSelected("basic") ? "kpm__selected kpm__selected-green" : ""}`}
            onClick={() => handleExpenseSelection("basic")}
          >
            <div>
              <div className="kpm__expense-card-header">
                <div className="kpm__expense-card-title-section">
                  <p className="kpm__expense-card-title">Basic</p>
                  <p className="kpm__expense-card-title-priority">Priority</p>
                </div>
              </div>
              <div className="kpm__expense-card-body">
                <p className="kpm__expense-card-description">
                  You can help a child at as low as <br />
                  <span className="kpm__expense-card-amount">
                    ₹ {totalBasicExpense}/month.
                  </span>
                </p>
              </div>
              <div
                className={`kpm__expense-card-pointers ${expandedCards.basic ? "kpm__visible" : ""}`}
              >
                <p className="kpm__expense-card-pointer">
                  {"\u2714"} School Enrollment
                </p>
                <p className="kpm__expense-card-pointer">
                  {"\u2714"} Continuous Learning
                </p>
              </div>
            </div>
            <button
              className="kpm__dropDownButton"
              onClick={(e) => {
                e.stopPropagation();
                toggleCardDropdown("basic");
              }}
            >
              <img
                src="/modalImages/dropDownVector.png"
                className={`kpm__dropDownImage ${expandedCards.basic ? "kpm__rotated" : ""}`}
              />
            </button>
          </button>

          <button
            className={`kpm__expense-card ${isExpenseSelected("educational") ? "kpm__selected kpm__selected-green" : ""}`}
            onClick={() => handleExpenseSelection("educational")}
          >
            <div>
              <div className="kpm__expense-card-header">
                <div className="kpm__expense-card-title-section">
                  <p className="kpm__expense-card-title">Educational</p>
                  <img
                    src="/modalImages/educationCard.png"
                    className="kpm__expense-card-title-priority-img"
                  />
                </div>
              </div>
              <div className="kpm__expense-card-body">
                <p className="kpm__expense-card-description">
                  Support their educational needs with <br />
                  <span className="kpm__expense-card-amount">
                    ₹ {totalEducationExpense}/month.
                  </span>
                </p>
              </div>
              <div
                className={`kpm__expense-card-pointers ${expandedCards.educational ? "kpm__visible" : ""}`}
              >
                <p className="kpm__expense-card-pointer">{"\u2714"} Books</p>
                <p className="kpm__expense-card-pointer">
                  {"\u2714"} Stationary
                </p>
              </div>
            </div>
            <button
              className="kpm__dropDownButton"
              onClick={(e) => {
                e.stopPropagation();
                toggleCardDropdown("educational");
              }}
            >
              <img
                src="/modalImages/dropDownVector.png"
                className={`kpm__dropDownImage ${expandedCards.educational ? "kpm__rotated" : ""}`}
              />
            </button>
          </button>

          <button
            className={`kpm__expense-card ${isExpenseSelected("co-curricular") ? "kpm__selected kpm__selected-green" : ""}`}
            onClick={() => handleExpenseSelection("co-curricular")}
          >
            <div>
              <div className="kpm__expense-card-header">
                <div className="kpm__expense-card-title-section">
                  <p className="kpm__expense-card-title">Co-curricular</p>
                  <img
                    src="/modalImages/coCurrCard.png"
                    className="kpm__expense-card-title-priority-img"
                  />
                </div>
              </div>
              <div className="kpm__expense-card-body">
                <p className="kpm__expense-card-description">
                  Enable their co-curricular activities with <br />
                  <span className="kpm__expense-card-amount">
                    ₹ {totalCurriculumExpense}/month.
                  </span>
                </p>
              </div>
              <div
                className={`kpm__expense-card-pointers ${expandedCards["co-curricular"] ? "kpm__visible" : ""}`}
              >
                <p className="kpm__expense-card-pointer">
                  {"\u2714"} Arts & Crafts
                </p>
                <p className="kpm__expense-card-pointer">
                  {"\u2714"} Sports Gear
                </p>
              </div>
            </div>
            <button
              className="kpm__dropDownButton"
              onClick={(e) => {
                e.stopPropagation();
                toggleCardDropdown("co-curricular");
              }}
            >
              <img
                src="/modalImages/dropDownVector.png"
                className={`kpm__dropDownImage ${expandedCards["co-curricular"] ? "kpm__rotated" : ""}`}
              />
            </button>
          </button>

          <button
            className={`kpm__expense-card ${isExpenseSelected("activity") ? "kpm__selected kpm__selected-green" : ""}`}
            onClick={() => handleExpenseSelection("activity")}
          >
            <div>
              <div className="kpm__expense-card-header">
                <div className="kpm__expense-card-title-section">
                  <p className="kpm__expense-card-title">Activity</p>
                  <img
                    src="/modalImages/activityCard.png"
                    className="kpm__expense-card-title-priority-img"
                  />
                </div>
              </div>
              <div className="kpm__expense-card-body">
                <p className="kpm__expense-card-description">
                  Support their extracurricular activities with{" "}
                  <span className="kpm__expense-card-amount">
                    ₹ {totalActivityExpense}/month.
                  </span>
                </p>
              </div>
              <div
                className={`kpm__expense-card-pointers ${expandedCards.activity ? "kpm__visible" : ""}`}
              >
                <p className="kpm__expense-card-pointer">
                  {"\u2714"} Sports Kits
                </p>
                <p className="kpm__expense-card-pointer">{"\u2714"} Picnics</p>
              </div>
            </div>
            <button
              className="kpm__dropDownButton"
              onClick={(e) => {
                e.stopPropagation();
                toggleCardDropdown("activity");
              }}
            >
              <img
                src="/modalImages/dropDownVector.png"
                className={`kpm__dropDownImage ${expandedCards.activity ? "kpm__rotated" : ""}`}
              />
            </button>
          </button>
        </div>
        <div className="kpm__expense-bottom">
          <div className="kpm__total-display">
            <p className="kpm__total-label">Total: </p>
            <p className="kpm__total-amount">₹ {selectedTotal}/month</p>
          </div>
          <button
            className="kpm__expense-bottom-button"
            style={{ alignItems: "center" }}
            onClick={handlePayment}
            disabled={paymentLoading || profile.is_sponsored || isSponsoredKid}
          >
            {profile.is_sponsored || isSponsoredKid
              ? "Already Sponsored"
              : paymentLoading
                ? "Processing..."
                : "Done"}
          </button>
        </div>
      </div>
    );
  };

  const PhotoModal = () => {
    const handlePrevPhoto = () => {
      setPhotoModalIndex((prevIndex) =>
        prevIndex === 0 ? kidPhotos.length - 1 : prevIndex - 1,
      );
    };

    const handleNextPhoto = () => {
      setPhotoModalIndex((prevIndex) =>
        prevIndex === kidPhotos.length - 1 ? 0 : prevIndex + 1,
      );
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsPhotoModalOpen(false);
      } else if (e.key === "ArrowLeft") {
        handlePrevPhoto();
      } else if (e.key === "ArrowRight") {
        handleNextPhoto();
      }
    };

    useEffect(() => {
      if (isPhotoModalOpen) {
        window.addEventListener("keydown", handleKeyDown);
        return () => {
          window.removeEventListener("keydown", handleKeyDown);
        };
      }
    }, [isPhotoModalOpen, photoModalIndex, kidPhotos.length]);

    if (!isPhotoModalOpen || kidPhotos.length === 0) return null;

    return (
      <div
        className="kpm__photo-modal-overlay"
        onClick={() => setIsPhotoModalOpen(false)}
      >
        <div
          className="kpm__photo-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="kpm__photo-modal-close"
            onClick={() => setIsPhotoModalOpen(false)}
            aria-label="Close photo modal"
          >
            <VscChromeClose size={28} color="#fff" />
          </button>

          {kidPhotos.length > 1 && (
            <button
              className="kpm__photo-modal-nav kpm__photo-modal-prev"
              onClick={handlePrevPhoto}
              aria-label="Previous photo"
            >
              <img src="./modalImages/leftSlider.png" alt="Previous" />
            </button>
          )}

          <img
            src={kidPhotos[photoModalIndex].photo_url}
            alt="Full view"
            className="kpm__photo-modal-image"
          />

          {kidPhotos.length > 1 && (
            <button
              className="kpm__photo-modal-nav kpm__photo-modal-next"
              onClick={handleNextPhoto}
              aria-label="Next photo"
            >
              <img src="./modalImages/rightSlider.png" alt="Next" />
            </button>
          )}

          {kidPhotos.length > 1 && (
            <div className="kpm__photo-modal-counter">
              {photoModalIndex + 1} / {kidPhotos.length}
            </div>
          )}
        </div>
      </div>
    );
  };

  const PhotosContent = () => {
    if (mediaLoading) {
      return <Loader />;
    }
    const handlePrevClick = () => {
      setCurrentPhotoIndex((prevIndex) =>
        prevIndex === 0 ? kidPhotos.length - 1 : prevIndex - 1,
      );
    };

    const handleNextClick = () => {
      setCurrentPhotoIndex((prevIndex) =>
        prevIndex === kidPhotos.length - 1 ? 0 : prevIndex + 1,
      );
    };

    if ((user_id && isSponsoredKid) || super_admin_id) {
      return (
        <div className="kpm__photos-container">
          {kidPhotos.length > 0 ? (
            <>
              <button
                className="kpm__slider-nav-button kpm__prev"
                onClick={handlePrevClick}
                aria-label="Previous photo"
              >
                <img src="./modalImages/leftSlider.png" />
              </button>

              <div className="kpm__photos-slider">
                {kidPhotos.map((photo, index) => (
                  <div
                    key={index}
                    className={`kpm__photo-item ${
                      (window.innerWidth >= 778 &&
                        index >= currentPhotoIndex &&
                        index < currentPhotoIndex + 4) ||
                      (window.innerWidth < 778 && index === currentPhotoIndex)
                        ? "kpm__visible"
                        : "kpm__hidden"
                    }`}
                  >
                    <img
                      src={photo.photo_url}
                      className="kpm__photo-img"
                      alt="kidimage"
                      onClick={() => {
                        setPhotoModalIndex(index);
                        setIsPhotoModalOpen(true);
                      }}
                    />
                  </div>
                ))}
              </div>

              <button
                className="kpm__slider-nav-button kpm__next"
                onClick={handleNextClick}
                aria-label="Next photo"
              >
                <img src="./modalImages/rightSlider.png" />
              </button>
            </>
          ) : (
            <h5
              style={{ color: "#231f20", fontSize: "18px", fontWeight: "bold" }}
            >
              No Photos Available
            </h5>
          )}
        </div>
      );
    } else {
      // If user is not logged in or does not sponsor the kid
      return (
        <div className="kpm__locked-content">
          <div className="kpm__locked-content-inner">
            <img
              src="/mentorLock.png"
              className="kpm__lockImage"
              alt="Locked Content"
            />
            <div className="kpm__locked-content-text">
              <p
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: "#293E4F",
                }}
              >
                Please Login as mentor to view photos
              </p>
              <p
                style={{ fontSize: "14px", fontWeight: 400, color: "#757575" }}
              >
                You can access photos only if you mentor the kid.
                <button
                  style={{
                    backgroundColor: "transparent",
                    border: "none",
                    borderBottom: "1px solid #293E4F",
                    color: "#293e4f",
                    fontWeight: 600,
                  }}
                  onClick={() => navigate("/login")}
                >
                  Login
                </button>
              </p>
            </div>
          </div>
        </div>
      );
    }
  };

  const DocumentsContent = () => {
    if (mediaLoading) {
      return <Loader />;
    }
    if ((user_id && isSponsoredKid) || super_admin_id) {
      return (
        <div className="kpm__documents-panel">
          <div className="kpm__documents-toolbar">
            <div className="kpm__documents-search-row">
              <div className="kpm__documents-search">
                <FaSearch className="kpm__documents-search-icon" aria-hidden />
                <input
                  type="search"
                  className="kpm__documents-search-input"
                  placeholder="Search receipts, certificates, reports, and photos"
                  value={documentSearch}
                  onChange={(e) => setDocumentSearch(e.target.value)}
                />
              </div>
              <select
                className="kpm__documents-sort"
                value={documentSort}
                onChange={(e) => setDocumentSort(e.target.value)}
                aria-label="Sort documents"
              >
                <option value="latest">Latest</option>
                <option value="oldest">Oldest</option>
                <option value="name">A–Z</option>
              </select>
              <div className="kpm__documents-filters">
                {DOCUMENT_FILTER_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`kpm__documents-filter-btn${
                      documentFilter === option.id
                        ? " kpm__documents-filter-btn--active"
                        : ""
                    }`}
                    onClick={() => setDocumentFilter(option.id)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <p className="kpm__documents-count">
              {visibleDocuments.length} document
              {visibleDocuments.length === 1 ? "" : "s"} available
            </p>
          </div>

          {visibleDocuments.length > 0 ? (
            <div className="kpm__documents-grid">
              {visibleDocuments.map((document) => (
                <div key={document.id} className="kpm__document-card">
                  <div className="kpm__document-card-icon" aria-hidden>
                    <HiOutlineDocumentText />
                  </div>
                  <div
                    className="kpm__document-card-title"
                    title={document.title}
                  >
                    {document.title}
                  </div>
                  <button
                    type="button"
                    className="kpm__document-card-view"
                    onClick={() => handleViewDocument(document.document_url)}
                    aria-label={`View ${document.title}`}
                  >
                    <FaEye />
                  </button>
                </div>
              ))}
            </div>
          ) : kidDocuments.length > 0 ? (
            <p className="kpm__documents-empty">
              No documents match your search or filter.
            </p>
          ) : (
            <p className="kpm__documents-empty">No Documents Available</p>
          )}
        </div>
      );
    } else {
      // If user is not logged in or does not sponsor the kid
      return (
        <div className="kpm__locked-content">
          <div className="kpm__locked-content-inner">
            <img
              src="/mentorLock.png"
              className="kpm__lockImage"
              alt="Locked Content"
            />
            <div className="kpm__locked-content-text">
              <p
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: "#293E4F",
                }}
              >
                Please Login as mentor to view documents
              </p>
              <p
                style={{ fontSize: "14px", fontWeight: 400, color: "#757575" }}
              >
                You can access documents only if you mentor the kid.
                <button
                  style={{
                    backgroundColor: "transparent",
                    border: "none",
                    borderBottom: "1px solid #293E4F",
                    color: "#293E4F",
                    fontWeight: 600,
                  }}
                  onClick={() => navigate("/login")}
                >
                  Login
                </button>
              </p>
            </div>
          </div>
        </div>
      );
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "profile":
        return <ProfileContent />;
      case "expenses":
        return <ExpenseContent />;
      case "photo":
        return <PhotosContent />;
      case "document":
        return <DocumentsContent />;
      default:
        return <ProfileContent />;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <PhotoModal />
      <div className="kpm__modal-overlay" onClick={handleClickOutside}>
        <div
          className="kpm__modal-container"
          ref={modalRef}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="kpm__modal-close-btn" onClick={onClose}>
            <VscChromeClose size={24} color="#fff" />
          </div>

          {loading ? (
            <Loader />
          ) : (
            <>
              <div className="kpm__profile-container kpm__modal-profile-container">
                <div className="kpm__profile-header">
                  <div className="ksm__desktopBg"></div>
                  {/* <img
                src="/modalImages/desktopProfileBg.png"
                alt="Background"
                className="kpm__profile-bg"
              /> */}
                  <img
                    src="/modalImages/mobileProfileBg.png"
                    alt="Background"
                    className="kpm__mobile-profile-bg"
                  />
                  <div className="kpm__profile-header-text">
                    <p className="kpm__profile-header-category">
                      {profile.category_name}
                    </p>
                    <h1 className="kpm__profile-header-name">
                      {profile.name}'s Profile
                    </h1>
                  </div>

                  <div className="kpm__profile-details">
                    <div className="kpm__kid-profile-avatar">
                      <img
                        src={profile.avatar_url || "../child/noImg.png"}
                        alt="profile-avatar"
                        className="rounded-circle"
                      />
                    </div>
                    <div className="kpm__profile-meta flex sorted">
                      <span>{profile.enrollment_id}</span>
                    </div>

                    <div className="kpm__profile-actions-wrap">
                      <div className="kpm__profile-actions kpm__profile-actions-buttons">
                        <button
                          type="button"
                          className="kpm__share-profile-btn"
                          onClick={() => setShareMenuOpen((o) => !o)}
                          aria-label="Share profile"
                        >
                          <IoShareSocial size={20} />
                          <span>Share Profile</span>
                        </button>
                        {profile.is_sponsored || isSponsoredKid ? (
                          <button className="btn btn-primary mb-3 kpm__sponsor-btn">
                            {profile.name} is Already Sponsored
                          </button>
                        ) : (
                          <button
                            onClick={handlePayment}
                            className="kpm__sponsorAnonymous kpm__sponsor-btn"
                            disabled={paymentLoading}
                          >
                            {paymentLoading
                              ? "Processing..."
                              : `Sponsor ${profile.name}`}
                          </button>
                        )}
                      </div>
                      {shareMenuOpen && (
                        <>
                          <div
                            className="kpm__share-backdrop"
                            onClick={() => setShareMenuOpen(false)}
                            aria-hidden="true"
                          />
                          <div className="kpm__share-menu kpm__share-menu-below-actions">
                            <a
                              href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="kpm__share-menu-item"
                              onClick={() => setShareMenuOpen(false)}
                            >
                              Share to WhatsApp
                            </a>
                            <button
                              type="button"
                              className="kpm__share-menu-item"
                              onClick={() => {
                                navigator.clipboard.writeText(sponsorLink);
                                setShareMenuOpen(false);
                              }}
                            >
                              Copy sponsor link
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="kpm__dashboard-Container"
                  style={{ marginTop: "60px" }}
                >
                  {/* Tab Navigation */}
                  <div className="kpm__tab-navigation">
                    <button
                      className={`kpm__tab-button ${activeTab === "profile" ? "kpm__active" : ""}`}
                      onClick={() => handleTabClick("profile")}
                      style={{
                        borderBottom:
                          activeTab === "profile"
                            ? "1px solid #299D8E"
                            : "1px solid #f6f6f6",
                      }}
                    >
                      Profile
                    </button>
                    <button
                      className={`kpm__tab-button ${activeTab === "expenses" ? "kpm__active" : ""}`}
                      onClick={() => handleTabClick("expenses")}
                      style={{
                        borderBottom:
                          activeTab === "expenses"
                            ? "1px solid #299D8E"
                            : "1px solid #f6f6f6",
                      }}
                    >
                      Expenses
                    </button>
                    <button
                      className={`kpm__tab-button ${activeTab === "photo" ? "kpm__active" : ""}`}
                      onClick={() => handleTabClick("photo")}
                      style={{
                        borderBottom:
                          activeTab === "photo"
                            ? "1px solid #299D8E"
                            : "1px solid #f6f6f6",
                      }}
                    >
                      Photo
                    </button>
                    <button
                      className={`kpm__tab-button ${activeTab === "document" ? "kpm__active" : ""}`}
                      onClick={() => handleTabClick("document")}
                      style={{
                        borderBottom:
                          activeTab === "document"
                            ? "1px solid #299D8E"
                            : "1px solid #f6f6f6",
                      }}
                    >
                      Document
                    </button>
                  </div>

                  {/* Tab Content Area */}
                  <div className="kpm__tab-content-container">
                    {renderTabContent()}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default KidProfileModal;
